import { useState, useCallback, useRef, useEffect } from 'react'
import { X, Upload, FileText, Loader2, CheckCircle2, AlertCircle, Search } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import {
  IntuiTabs, IntuiTabsList, IntuiTabsTrigger, IntuiTabsContent,
} from '@/components/intueri/IntuiTabs'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiSelect, IntuiSelectContent, IntuiSelectItem,
  IntuiSelectTrigger, IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import { Checkbox } from '@/components/ui/checkbox'
import { config } from '@/config'
import { ensureFreshToken } from '@/config/keycloakClient'
import { CORRELATION_ID_HEADER } from '@/constants/headers'
import { generateCorrelationId } from '@/utils/correlationId'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'
import type { DocumentRecord } from '@/types/catalog'

type FileStatus = 'pending' | 'uploading' | 'complete' | 'error'

interface FileEntry {
  id: string
  file: File
  name: string
  status: FileStatus
  progress: number
}

interface AddKnowledgeModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * TODO: forward to the upload + catalog-select endpoints once the backend
   * exposes a "link documents to digital coworker" API. Currently captured
   * for the future wiring but not transmitted anywhere.
   */
  coworkerId?: string
  /** Called with the IDs of newly uploaded + selected catalog docs after the user confirms. */
  onSubmitted?: (docIds: string[]) => void
}

const DOC_TYPES = ['all', 'pnid', 'pfd', 'document', 'datasheet', 'timeseries'] as const
const DOC_TYPE_LABEL: Record<string, string> = {
  all: 'All Types',
  pnid: 'P&ID',
  pfd: 'PFD',
  document: 'Document',
  datasheet: 'Table',
  timeseries: 'Time Series',
}

const PAGE_SIZE = 10

async function uploadFileForKnowledge(
  file: File,
  onProgress: (percent: number) => void,
): Promise<string> {
  const token = await ensureFreshToken()
  const correlationId = generateCorrelationId()

  return new Promise<string>((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('filename', file.name)
    formData.append('fileType', 'document')
    formData.append('upload_started_at', new Date().toISOString())
    formData.append('auto_rename', 'true')

    const xhr = new XMLHttpRequest()
    const baseUrl = config.apiBaseUrl.replace(/\/+$/, '')
    xhr.open('POST', `${baseUrl}/v2/files/upload`)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader(CORRELATION_ID_HEADER, correlationId)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.min(95, Math.round((e.loaded / e.total) * 95)))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText) as { document?: { id?: string }; id?: string }
          resolve(data.document?.id || data.id || '')
        } catch {
          resolve('')
        }
      } else {
        let msg = `Upload failed (${xhr.status})`
        try {
          const data = JSON.parse(xhr.responseText) as { detail?: string; error?: string; message?: string }
          msg = data.detail || data.error || data.message || msg
        } catch { /* keep default */ }
        reject(new Error(msg))
      }
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new Error('Upload cancelled'))
    xhr.send(formData)
  })
}

export default function AddKnowledgeModal({ isOpen, onClose, onSubmitted }: AddKnowledgeModalProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'add' | 'catalog'>('add')

  // Add tab state
  const [files, setFiles] = useState<FileEntry[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCountRef = useRef(0)

  // Data Catalog tab state
  const [catalogDocs, setCatalogDocs] = useState<DocumentRecord[]>([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogTotal, setCatalogTotal] = useState(0)
  const [catalogPage, setCatalogPage] = useState(1)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [catalogType, setCatalogType] = useState<string>('all')
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTab('add')
      setFiles([])
      setIsDragging(false)
      setIsUploading(false)
      setSelectedDocIds(new Set())
      setCatalogPage(1)
      setCatalogSearch('')
      setDebouncedSearch('')
      setCatalogType('all')
      dragCountRef.current = 0
    }
  }, [isOpen])

  // Debounce search input so we don't fire a request on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(catalogSearch), 300)
    return () => window.clearTimeout(id)
  }, [catalogSearch])

  // Load catalog when tab opens or filters change
  useEffect(() => {
    if (!isOpen || tab !== 'catalog') return
    let cancelled = false
    setCatalogLoading(true)
    apiService
      .listDocsPaginated({
        page_number: catalogPage,
        page_size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        type: catalogType === 'all' ? undefined : catalogType,
      })
      .then((res) => {
        if (cancelled) return
        setCatalogDocs(res.documents)
        setCatalogTotal(res.total)
      })
      .catch(() => {
        if (cancelled) return
        toast.error(t('digital_coworker:add_knowledge.errors.catalog_load', 'Failed to load data catalog'))
      })
      .finally(() => { if (!cancelled) setCatalogLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, tab, catalogPage, debouncedSearch, catalogType, t])

  const addFiles = useCallback((newFiles: File[]) => {
    const entries: FileEntry[] = newFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      status: 'pending',
      progress: 0,
    }))
    setFiles((prev) => [...prev, ...entries])
  }, [])

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : []
    if (selected.length > 0) addFiles(selected)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [addFiles])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    dragCountRef.current++
    if (dragCountRef.current === 1) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    dragCountRef.current--
    if (dragCountRef.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    dragCountRef.current = 0
    setIsDragging(false)
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length > 0) addFiles(dropped)
  }, [addFiles])

  // Upload all pending files in parallel; resolve with their document ids.
  const handleAddSubmit = useCallback(async () => {
    const pending = files.filter((f) => f.status === 'pending')
    if (pending.length === 0) return
    setIsUploading(true)

    const uploadedIds: string[] = []
    let failedCount = 0
    await Promise.all(pending.map(async (entry) => {
      setFiles((prev) => prev.map((f) =>
        f.id === entry.id ? { ...f, status: 'uploading', progress: 0 } : f))
      try {
        const docId = await uploadFileForKnowledge(entry.file, (percent) => {
          setFiles((prev) => prev.map((f) =>
            f.id === entry.id ? { ...f, progress: percent } : f))
        })
        if (docId) uploadedIds.push(docId)
        setFiles((prev) => prev.map((f) =>
          f.id === entry.id ? { ...f, status: 'complete', progress: 100 } : f))
      } catch (err) {
        failedCount++
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setFiles((prev) => prev.map((f) =>
          f.id === entry.id ? { ...f, status: 'error', progress: 0 } : f))
        toast.error(`${entry.name}: ${msg}`)
      }
    }))

    setIsUploading(false)
    if (uploadedIds.length > 0) {
      onSubmitted?.(uploadedIds)
      toast.success(t('digital_coworker:add_knowledge.upload_success', {
        defaultValue: 'Added {{count}} file(s) as knowledge',
        count: uploadedIds.length,
      }))
    }
    // Keep the modal open if any uploads failed so the user can retry or
    // remove the failed entries instead of losing the list on auto-close.
    if (failedCount === 0 && uploadedIds.length > 0) {
      onClose()
    }
  }, [files, onSubmitted, onClose, t])

  const handleCatalogSubmit = useCallback(() => {
    if (selectedDocIds.size === 0) return
    onSubmitted?.([...selectedDocIds])
    toast.success(t('digital_coworker:add_knowledge.select_success', {
      defaultValue: 'Selected {{count}} document(s)',
      count: selectedDocIds.size,
    }))
    onClose()
  }, [selectedDocIds, onSubmitted, onClose, t])

  const toggleDocSelection = useCallback((id: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const totalPages = Math.max(1, Math.ceil(catalogTotal / PAGE_SIZE))
  const pendingCount = files.filter((f) => f.status === 'pending').length
  const hasFiles = files.length > 0

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => { if (!open && !isUploading) onClose() }}
    >
      <DialogContent className="sm:max-w-[680px] p-0 gap-0" hideDefaultClose>
        <DialogDescription className="sr-only">{t('digital_coworker:add_knowledge.dialog_description', { defaultValue: 'Add knowledge sources to your digital coworker' })}</DialogDescription>
        <DialogTitle className="sr-only">{t('digital_coworker:add_knowledge.dialog_title', { defaultValue: 'Add Knowledge' })}</DialogTitle>

        {/* Single IntuiTabs root so Radix can wire aria-controls/aria-labelledby
            from the triggers to the matching content panels. */}
        <IntuiTabs value={tab} onValueChange={(v) => setTab(v as 'add' | 'catalog')}>
          <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-border">
            <IntuiTabsList variant="line">
              <IntuiTabsTrigger value="add">
                {t('digital_coworker:add_knowledge.tab_add', 'Add')}
              </IntuiTabsTrigger>
              <IntuiTabsTrigger value="catalog">
                {t('digital_coworker:add_knowledge.tab_catalog', 'Data Catalog')}
              </IntuiTabsTrigger>
            </IntuiTabsList>
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              aria-label={t('digital_coworker:add_knowledge.close', 'Close')}
              className="rounded-sm opacity-70 hover:opacity-100 transition-opacity disabled:opacity-40"
            >
              <X className="size-4" />
            </button>
          </div>

          {/* Add tab */}
          <IntuiTabsContent value="add" className="m-0">
            <div className="px-5 py-5">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
              <div
                onClick={handleDropZoneClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-12 cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-[#22d3ee] dark:border-[#22d3ee] hover:border-[#0e7490]'
                }`}
              >
                <Upload className="size-5 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {t('digital_coworker:add_knowledge.drop_hint', 'Drag and drop files here, or click to select files')}
                </span>
              </div>

              {hasFiles && (
                <div className="mt-4 flex flex-col gap-2 max-h-[240px] overflow-y-auto overflow-x-hidden">
                  {files.map((entry) => (
                    <div key={entry.id} className="flex items-center gap-2 min-w-0">
                      <FileText className="size-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <span className="text-sm truncate block">{entry.name}</span>
                        {entry.status === 'uploading' && (
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1.5 bg-[#e5e5e5] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full transition-[width] duration-200 ease-linear"
                                style={{ width: `${entry.progress}%` }}
                              />
                            </div>
                            <span className="text-xs tabular-nums text-muted-foreground shrink-0 w-8 text-right">
                              {entry.progress}%
                            </span>
                          </div>
                        )}
                      </div>
                      {entry.status === 'uploading' ? (
                        <Loader2 className="size-4 text-muted-foreground animate-spin shrink-0" />
                      ) : entry.status === 'complete' ? (
                        <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                      ) : entry.status === 'error' ? (
                        <AlertCircle className="size-4 text-red-500 shrink-0" />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeFile(entry.id) }}
                          className="opacity-50 hover:opacity-100 transition-opacity shrink-0"
                          aria-label={t('digital_coworker:add_knowledge.remove_file', { name: entry.name, defaultValue: 'Remove {{name}}' })}
                        >
                          <X className="size-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {hasFiles && (
                <div className="mt-4 flex justify-end">
                  <IntuiButton
                    onClick={handleAddSubmit}
                    disabled={pendingCount === 0 || isUploading}
                    loading={isUploading}
                  >
                    {t('digital_coworker:add_knowledge.add_button', {
                      defaultValue: 'Add Data ({{count}})',
                      count: pendingCount,
                    })}
                  </IntuiButton>
                </div>
              )}
            </div>
          </IntuiTabsContent>

          {/* Data Catalog tab */}
          <IntuiTabsContent value="catalog" className="m-0">
            <div className="px-5 py-4 flex flex-col gap-3">
              {/* Filters row */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <IntuiInput
                    placeholder={t('common:search', 'Search')}
                    value={catalogSearch}
                    onChange={(e) => { setCatalogSearch(e.target.value); setCatalogPage(1) }}
                    className="h-9 ps-9"
                  />
                </div>
                <IntuiSelect value={catalogType} onValueChange={(v) => { setCatalogType(v); setCatalogPage(1) }}>
                  <IntuiSelectTrigger className="w-[140px] h-9">
                    <IntuiSelectValue />
                  </IntuiSelectTrigger>
                  <IntuiSelectContent>
                    {DOC_TYPES.map((docType) => (
                      <IntuiSelectItem key={docType} value={docType}>{DOC_TYPE_LABEL[docType]}</IntuiSelectItem>
                    ))}
                  </IntuiSelectContent>
                </IntuiSelect>
              </div>

              {/* Table */}
              <div className="rounded-md border border-border overflow-hidden">
                {/* table-fixed so w-full + per-column widths are honored and the
                    name/user `truncate` engages — otherwise long filenames widen
                    the auto-layout table past the modal and clip User (FS-545). */}
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-muted/30 text-muted-foreground text-xs">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left">
                        <span className="sr-only">{t('digital_coworker:add_knowledge.select_column', { defaultValue: 'Select' })}</span>
                      </th>
                      <th className="px-3 py-2 text-left font-medium">{t('common:name', 'Name')}</th>
                      <th className="px-3 py-2 text-left font-medium w-[100px]">{t('common:type', 'Type')}</th>
                      <th className="px-3 py-2 text-left font-medium w-[140px]">{t('common:user', 'User')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogLoading ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          <Loader2 className="size-4 inline-block animate-spin mr-2 align-middle" />
                          {t('common:loading', 'Loading...')}
                        </td>
                      </tr>
                    ) : catalogDocs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('digital_coworker:add_knowledge.no_docs', 'No documents found')}
                        </td>
                      </tr>
                    ) : (
                      catalogDocs.map((doc) => {
                        const checked = selectedDocIds.has(doc.id)
                        return (
                          <tr
                            key={doc.id}
                            className="border-t border-border hover:bg-muted/30 cursor-pointer"
                            onClick={() => toggleDocSelection(doc.id)}
                          >
                            <td className="px-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleDocSelection(doc.id)}
                                aria-label={t('digital_coworker:add_knowledge.select_doc', { name: doc.name, defaultValue: 'Select {{name}}' })}
                              />
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="size-4 text-muted-foreground shrink-0" />
                                <span className="truncate">{doc.name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 align-middle text-muted-foreground">
                              {DOC_TYPE_LABEL[doc.type] || doc.type}
                            </td>
                            <td className="px-3 py-2 align-middle text-muted-foreground truncate">
                              {doc.uploader_name || '—'}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-1">
                  <IntuiButton
                    variant="outline"
                    size="sm"
                    onClick={() => setCatalogPage((p) => Math.max(1, p - 1))}
                    disabled={catalogPage <= 1 || catalogLoading}
                  >
                    {t('common:previous', 'Previous')}
                  </IntuiButton>
                  <span className="text-xs text-muted-foreground px-2">
                    {catalogPage} / {totalPages}
                  </span>
                  <IntuiButton
                    variant="outline"
                    size="sm"
                    onClick={() => setCatalogPage((p) => Math.min(totalPages, p + 1))}
                    disabled={catalogPage >= totalPages || catalogLoading}
                  >
                    {t('common:next', 'Next')}
                  </IntuiButton>
                </div>
                <IntuiButton
                  onClick={handleCatalogSubmit}
                  disabled={selectedDocIds.size === 0}
                >
                  {t('digital_coworker:add_knowledge.select_button', {
                    defaultValue: 'Select Data ({{count}})',
                    count: selectedDocIds.size,
                  })}
                </IntuiButton>
              </div>
            </div>
          </IntuiTabsContent>
        </IntuiTabs>
      </DialogContent>
    </Dialog>
  )
}
